import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import * as Sentry from '@sentry/react';

import { FormModal } from '@/components/form-modal';
import { EdlinkOrgChartDryRunResult, EdlinkOrgChartImportSummary } from '@airreading/common/data';
import { exportDryRunToCSV, exportSummaryToCSV } from '@/lib/edlink-csv';
import { airServerErrorFrom } from '@/lib/error-handling';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { EdlinkIdCode } from '@/components/alphanumeric-code';
import { EdlinkSyncResult, EdlinkSyncResultView } from './edlink-sync-result-view';

const formSchema = z.object({});

type SyncEdlinkModalBaseProps = {
  modalTitle: string;
  entityName: string;
  entityId: string;
  trigger: React.ReactNode;
  syncFn: () => Promise<EdlinkOrgChartImportSummary>;
  onSyncSuccess?: () => void;
  syncSentryTags: Record<string, string>;
  syncErrorMessage: string;
};

// If dryRunFn and dryRunSentryTags are provided, the modal will have the option to perform a dry run before syncing
type SyncEdlinkModalProps = SyncEdlinkModalBaseProps &
  (
    | { dryRunFn: () => Promise<EdlinkOrgChartDryRunResult>; dryRunSentryTags: Record<string, string> }
    | { dryRunFn?: undefined; dryRunSentryTags?: undefined }
  );

export const SyncEdlinkModal = ({
  modalTitle,
  entityName,
  entityId,
  trigger,
  syncFn,
  dryRunFn,
  onSyncSuccess,
  syncSentryTags,
  dryRunSentryTags,
  syncErrorMessage,
}: SyncEdlinkModalProps) => {
  const { mutateAsync, isPending: isSyncPending } = useMutation({ mutationFn: syncFn });

  const runDryRun = () => {
    if (!dryRunFn) throw new Error('Dry run is not enabled for this action.');
    return dryRunFn();
  };
  const { mutateAsync: dryRunMutate, isPending: isDryRunPending } = useMutation({ mutationFn: runDryRun });

  const [syncResult, setSyncResult] = useState<EdlinkSyncResult | null>(null);
  const [dryRunResult, setDryRunResult] = useState<EdlinkSyncResult | null>(null);
  const [confirmPending, setConfirmPending] = useState(false);

  const hasSuccess = syncResult?.type === 'success' || dryRunResult?.type === 'success';

  const onSubmit = () => setConfirmPending(true);

  const onConfirmSync = async () => {
    setConfirmPending(false);

    try {
      const summary = await mutateAsync();
      setDryRunResult(null);
      setSyncResult({ type: 'success', summary, entityName, entityId });
      onSyncSuccess?.();
    } catch (e: unknown) {
      Sentry.captureException(e, { tags: syncSentryTags });
      const { userFacingMessage } = airServerErrorFrom(e) || {};
      const errorReason = userFacingMessage ? `Reason: ${userFacingMessage}` : '';
      setSyncResult({ type: 'error', message: [syncErrorMessage, errorReason].filter(Boolean).join(' ') });
    }
  };

  const onClose = () => {
    setSyncResult(null);
    setDryRunResult(null);
  };

  const onDownloadSummary = () => {
    const result = syncResult?.type === 'success' ? syncResult : dryRunResult?.type === 'success' ? dryRunResult : null;

    if (!result) {
      return;
    }

    const { summary } = result;

    if (summary instanceof EdlinkOrgChartImportSummary) {
      exportSummaryToCSV(summary, `${entityName} - ${entityId} - Sync Summary.csv`);
    } else {
      exportDryRunToCSV(summary, `${entityName} - ${entityId} - Dry Run Summary.csv`);
    }
  };

  const onDryRun = () => {
    setDryRunResult(null);
    setSyncResult(null);

    dryRunMutate()
      .then((summary) => setDryRunResult({ type: 'success', summary, entityName, entityId }))
      .catch((e: unknown) => {
        Sentry.captureException(e, { tags: dryRunSentryTags });
        const { userFacingMessage } = airServerErrorFrom(e) || {};
        const errorReason = userFacingMessage ? `Reason: ${userFacingMessage}` : '';

        setDryRunResult({
          type: 'error',
          message: ['Dry run failed. Please try again.', errorReason].filter(Boolean).join(' '),
        });
      });
  };

  const confirmTitle = dryRunFn ? (dryRunResult ? 'Confirm Sync' : 'Skip test and sync?') : 'Sync from Edlink?';

  const confirmDescription = dryRunFn
    ? dryRunResult
      ? 'This action will sync the records based on the current dry run result.'
      : 'You are about to sync data without running a test. This might introduce incorrect records.'
    : 'This action will fetch the latest data from Edlink and sync any changes.';

  return (
    <>
      <AlertDialog open={confirmPending} onOpenChange={(open) => !open && setConfirmPending(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>{confirmDescription} Please confirm before proceeding.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmSync}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <FormModal
        modalTitle={modalTitle}
        onSubmit={onSubmit}
        onClose={onClose}
        confirmBtnLabel="Sync"
        hideCancelButton
        submitButtonProps={{ loading: isSyncPending, disabled: isDryRunPending || isSyncPending }}
        formSchema={formSchema}
        trigger={trigger}
        isFullScreen={hasSuccess}
        preventExternalClose
        footerContent={() => (
          <>
            {hasSuccess && (
              <Button
                type="button"
                variant="secondary"
                disabled={isDryRunPending || isSyncPending}
                onClick={onDownloadSummary}
              >
                {syncResult?.type === 'success' ? 'Download Summary' : 'Download Dry Run Summary'}
              </Button>
            )}
            {dryRunFn && (
              <Button
                type="button"
                variant="secondary"
                loading={isDryRunPending}
                disabled={isDryRunPending || isSyncPending}
                onClick={onDryRun}
              >
                Dry Run
              </Button>
            )}
          </>
        )}
      >
        {() => (
          <div className="grid gap-4 py-4">
            <div className="text-sm">
              <span className="font-medium">{entityName}</span> -{' '}
              <EdlinkIdCode code={entityId} showCopyButton={false} wrapText={false} />
            </div>
            {dryRunFn && <EdlinkSyncResultView result={dryRunResult} />}
            <EdlinkSyncResultView result={syncResult} />
          </div>
        )}
      </FormModal>
    </>
  );
};
